// @tailwind
import { Box, Button, Collapse, Fade, Slide } from '@mui/material';
import DDMNavBar from '@/externals/ddm/components/DDMNavBar';
import MenuDrawer, { MenuItem, MenuTitleListItem } from '@/layouts/ManagerLayout/MenuDrawer';
import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useParams, ScrollRestoration, useNavigation, useRevalidator } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
    Info as InfoIcon,
    ProductionQuantityLimits as ProductIcon,
    ReceiptLong as OrderListIcon,
    LocalShipping as ShippingIcon,
    SupportAgent as VocIcon,
    SettingsOutlined as SettingIcon,
    Dashboard as TemplateIcon,
    NewReleases,
    Troubleshoot,
    RecommendOutlined,
    ForumOutlined,
    EventNote as ScheduleIcon,
} from '@mui/icons-material';

import { env } from '@/constant/configure';

import Footer from '@/components/common/Footer';
import { useSessionStore, useSpaceUserV2Store } from '@/store';
import ScreenOperator from '@/components/common/ScreenOperator';
import PageLoadingScreen from '@/components/PageLoadingScreen';
import Banner from '@/layouts/components/Banner';
import { DialogContainer } from '@/modules/MuiDialog';
import tempProductImg from '@/assets/images/character/pudding_viva.gif';

import vendorProductImg from '@/assets/images/sources/seller_vendor_product.png';
import myProductImg from '@/assets/images/sources/seller_my_product.png';
import sellerSalesImg from '@/assets/images/sources/seller_sales.png';
import sodomallLogo from '@/assets/images/logo/sodomall_logo.png';

const AppendExample = () => (
    <Box
        sx={{
            backgroundColor: 'var(--v-palette-black-main)',
            color: 'var(--v-palette-black-contrastText)',
            px: 3,
            py: 2,
        }}
    >
        <Button href={'/about'} variant={'contained'} color={'white'} size={'small'} fullWidth sx={{ my: 1 }}>
            <div className={'flex justify-between w-full'}>
                <span>
                    <SettingIcon />
                </span>
                <span className={'grow text-center'}>사이드바 하단 예시</span>
            </div>
        </Button>
    </Box>
);

const ManagerLayout = () => {
    const { t } = useTranslation();
    const [appBarHeight, setAppBarHeight] = useState(64);
    const [drawWidth, setDrawWidth] = useState(240);
    const [isDrawerOpen, setIsDrawerOpen] = useState(true);
    const [isBannerOpen, setIsBannerOpen] = useState(false);
    const drawRef = useRef<HTMLDivElement>(null);
    const params = useParams();

    const session = useSessionStore();
    const mallId = params.mallId || session.mallId || '';
    const { grade_name } = useSpaceUserV2Store();
    const isSodomall = grade_name === '소도몰';

    const { state } = useNavigation();

    const main_menu: MenuItem[] = [
        {
            label: t('sidebar.sellerInfo'),
            icon: <InfoIcon />,
            isActive: false,
            isShowDivider: true,
            children: [
                {
                    label: t('sidebar.sellerInformation'),
                    to: `/mall/${mallId || '-'}`,
                },
                // {
                //     label: '판매 정책 관리',
                //     to: `/mall/${mallId || '-'}/policy/sales-policy`
                // },
                // 배송 정책 관리 - 소도몰 역할일 때 숨김
                ...(!isSodomall
                    ? [
                          {
                              label: t('sidebar.shippingPolicyManagement'),
                              to: `/mall/${mallId || '-'}/policy/shipping-policy`,
                          },
                      ]
                    : []),
                // 공지사항 관리 - 소도몰 역할일 때 숨김
                ...(!isSodomall
                    ? [
                          {
                              label: t('sidebar.noticeManagement'),
                              to: `/mall/${mallId || '-'}/notices`,
                          },
                      ]
                    : []),
            ],
        },
        // 상품관리 - 소도몰 역할일 때 숨김
        ...(!isSodomall
            ? [
                  {
                      label: t('sidebar.productManagement'),
                      icon: <ProductIcon />,
                      isShowDivider: true,
                      isActive: false,
                      children: [
                          {
                              label: t('sidebar.tagCategoryManagement'),
                              to: `/mall/${mallId || '-'}/tag-manager`,
                          },
                          {
                              label: t('sidebar.productManagement'),
                              to: `/mall/${mallId || '-'}/products`,
                              isExistMatch: false,
                          },
                      ],
                  },
              ]
            : []),
        // {
        //     label: '주문 관리',
        //     icon: <OrderListIcon />,
        //     isShowDivider: true,
        //     isActive: false,
        //     to: `/mall/${mallId || '-'}/order-groups`,
        // },
        // {
        //     label: '배송 및 이슈 관리',
        //     icon: <ShippingIcon />,
        //     isShowDivider: true,
        //     isActive: false,
        //     children: [
        //         {
        //             label: '배송 관리',
        //             to: `/mall/${mallId || '-'}/shipping-managements`,
        //         },
        //         {
        //             label: '환불 및 교환',
        //             to: `/mall/${mallId || '-'}/return-orders`,
        //         },
        //     ],
        // },
        // 주문 / 배송관리 - 소도몰 역할일 때 숨김
        ...(!isSodomall
            ? [
                  {
                      label: t('sidebar.orderShippingManagement'),
                      icon: <OrderListIcon />,
                      isShowDivider: true,
                      isActive: false,
                      children: [
                          {
                              label: t('sidebar.orderManagement'),
                              to: `/mall/${mallId || '-'}/order-groups`,
                          },
                          {
                              label: t('sidebar.shippingManagement'),
                              to: `/mall/${mallId || '-'}/shipping-managements`,
                          },
                      ],
                  },
              ]
            : []),
        // 취소 / 환불 관리 - 소도몰 역할일 때 숨김
        ...(!isSodomall
            ? [
                  {
                      label: t('sidebar.cancelRefundManagement'),
                      icon: <OrderListIcon />,
                      isShowDivider: true,
                      isActive: false,
                      children: [
                          {
                              label: t('sidebar.cancelReturnExchangeManagement'),
                              to: `/mall/${mallId || '-'}/issue-orders`,
                          },
                          {
                              label: t('sidebar.refundManagement'),
                              to: `/mall/${mallId || '-'}/refund-orders`,
                          },
                      ],
                  },
              ]
            : []),
        // 고객 관리 - 소도몰 역할일 때 숨김
        ...(!isSodomall
            ? [
                  {
                      label: t('sidebar.customerManagement'),
                      icon: <VocIcon />,
                      isShowDivider: true,
                      isActive: false,
                      children: [
                          {
                              label: t('sidebar.customerManagement'),
                              to: `/mall/${mallId || '-'}/customers`,
                          },
                          {
                              label: t('sidebar.oneOnOneInquiryManagement'),
                              to: `/mall/${mallId || '-'}/inquiries`,
                          },
                      ],
                  },
              ]
            : []),
    ];

    const menu_dev: MenuItem[] = [
        ...main_menu,
        {
            label: t('sidebar.templateDemo'),
            icon: <TemplateIcon />,
            isActive: false,
            children: [
                {
                    label: t('sidebar.typography'),
                    to: '/template-demo',
                },
                {
                    label: t('sidebar.toastEditor'),
                    to: '/template-demo/toast-editor',
                },
                {
                    label: t('sidebar.dialogs'),
                    to: '/template-demo/dialog',
                },
                {
                    label: t('sidebar.buttons'),
                    to: '/template-demo/button',
                },
                {
                    label: t('sidebar.customFields'),
                    to: '/template-demo/custom-fields',
                },
                {
                    label: t('sidebar.dragAndDrop'),
                    to: '/template-demo/drag-and-drop',
                },
                {
                    label: t('sidebar.banner'),
                    to: '/template-demo/banner-demo',
                },
            ],
        },
    ];
    const vendor_product_menu: MenuItem[] = [
        // 판매자 정보 - 소도몰 역할일 때만 공구 관리 메뉴 상단에 표시
        ...(isSodomall
            ? [
                  {
                      label: t('sidebar.sellerInfo'),
                      icon: <InfoIcon />,
                      isActive: false,
                      isShowDivider: true,
                      to: `/mall/${mallId || '-'}`,
                  },
              ]
            : []),
        // 공구 생성 - 소도몰 역할일 때만 표시
        ...(isSodomall
            ? [
                  {
                      label: t('sidebar.createGroupBuy'),
                      icon: <ScheduleIcon />,
                      isShowDivider: true,
                      isActive: false,
                      to: `/mall/${mallId || '-'}/create-contract-sodomall`,
                  },
              ]
            : []),
        {
            label: t('sidebar.groupBuyProductManagement'),
            icon: <ProductIcon />,
            isShowDivider: true,
            isActive: false,
            to: isSodomall
                ? `/mall/${mallId || '-'}/group-sale-products-sodomall`
                : `/mall/${mallId || '-'}/group-sale-products`,
        },
        // {
        //     label: '공구상품 추천',
        //     icon: <RecommendOutlined />,
        //     isShowDivider: true,
        //     isActive: false,
        //     to: `/mall/${mallId || '-'}/recommend-products`
        // },
        {
            label: t('sidebar.groupBuyOrderStatus'),
            icon: <OrderListIcon />,
            isShowDivider: true,
            isActive: false,
            to: isSodomall
                ? `/mall/${mallId || '-'}/group-sales-orders-sodomall`
                : `/mall/${mallId || '-'}/group-sales-orders`,
        },
        // 채팅 - 소도몰 역할이 아닐 때만 표시
        ...(!isSodomall
            ? [
                  {
                      label: t('sidebar.chat'),
                      isShowDivider: true,
                      isActive: false,
                      icon: <ForumOutlined />,
                      to: `/mall/${mallId || '-'}/seller-chat`,
                  },
              ]
            : []),
    ];

    const sales_menu: MenuItem[] = [
        {
            label: t('sidebar.salesManagement'),
            isShowDivider: true,
            isActive: false,
            to: isSodomall
                ? `/mall/${mallId || '-'}/store-statement/sales-list`
                : `/mall/${mallId || '-'}/statement/sales-list`,
        },
        // { label: '정산내역', isShowDivider: true, isActive: false, to: `/mall/${mallId || '-'}/statement/calculate-list` },
        // { label: '부가세신고 내역', isShowDivider: true, isActive: false, to: `/mall/${mallId || '-'}/statement/vat-report-list` },
        // { label: '세금계산서 내역', isShowDivider: true, isActive: false, to: `/mall/${mallId || '-'}/statement/tax-bill-list` },
        // { label: '판매예치금 관리', isShowDivider: true, isActive: false, to: `/mall/${mallId || '-'}/statement/management-sales-deposit` },
    ];

    const menuTitleList: MenuTitleListItem[] = [
        // 매장 상품 - 소도몰 역할일 때 숨김
        ...(!isSodomall
            ? [
                  {
                      menuTitle: t('sidebar.myProducts'),
                      menuTitleImg: myProductImg,
                      menuList: env === 'prd' ? main_menu : menu_dev,
                  },
              ]
            : []),
        {
            menuTitle: isSodomall ? '공구 관리' : t('sidebar.groupBuyProducts'),
            menuTitleImg: vendorProductImg,
            menuList: vendor_product_menu,
        },
        { menuTitle: t('sidebar.settlementManagement'), menuList: sales_menu, menuTitleImg: sellerSalesImg },
    ];

    useEffect(() => {
        if (!!drawRef.current) {
            setDrawWidth(!!drawRef?.current ? drawRef?.current?.clientWidth : 0);
        }
    });

    // 사용자 역할에 따른 브라우저 타이틀, 파비콘, 테마 색상 변경
    useEffect(() => {
        const originalTitle = document.title;
        const originalFavicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
        const originalFaviconHref = originalFavicon?.href;

        // 파비콘 설정 함수
        const setFavicon = (href: string) => {
            let favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
            if (!favicon) {
                favicon = document.createElement('link');
                favicon.rel = 'icon';
                document.head.appendChild(favicon);
            }
            favicon.href = href;
        };

        if (isSodomall) {
            document.title = '소도몰 매장 관리자';
            setFavicon(sodomallLogo);

            // 소도몰 테마 색상 적용 (CSS 변수 오버라이드)
            const root = document.documentElement;
            // primary 색상 (#363bc2 = rgb(54, 59, 194))
            root.style.setProperty('--v-palette-primary-main', '#363bc2');
            root.style.setProperty('--v-palette-primary-light', '#5a5fd4');
            root.style.setProperty('--v-palette-primary-dark', '#2a2d8a');
            root.style.setProperty('--v-palette-primary-contrastText', '#fff');
            root.style.setProperty('--v-palette-primary-mainChannel', '54 59 194');
            root.style.setProperty('--v-palette-primary-lightChannel', '90 95 212');
            root.style.setProperty('--v-palette-primary-darkChannel', '42 45 138');
            // sellerColor 색상
            root.style.setProperty('--v-palette-sellerColor-main', '#363bc2');
            root.style.setProperty('--v-palette-sellerColor-dark', '#2a2d8a');
            root.style.setProperty('--v-palette-sellerColor-contrastText', '#fff');
            root.style.setProperty('--v-palette-sellerColor-mainChannel', '54 59 194');
            // deeper-orange 색상 (파란색으로 변경)
            root.style.setProperty('--v-palette-deeper-orange-main', '#363bc2');
            root.style.setProperty('--v-palette-deeper-orange-light', '#5a5fd4');
            root.style.setProperty('--v-palette-deeper-orange-dark', '#2a2d8a');
            root.style.setProperty('--v-palette-deeper-orange-contrastText', '#fff');
            root.style.setProperty('--v-palette-deeper-orange-mainChannel', '54 59 194');
        } else {
            // 일반 사용자: 기본 타이틀과 favicon 설정
            document.title = 'DDM Live';
            setFavicon('/ddm_live_logo_48.jpg');
        }

        return () => {
            // 컴포넌트 언마운트 시 원래대로 복원
            if (isSodomall) {
                document.title = originalTitle;
                if (originalFavicon && originalFaviconHref) {
                    originalFavicon.href = originalFaviconHref;
                }

                // CSS 변수 복원
                const root = document.documentElement;
                root.style.removeProperty('--v-palette-primary-main');
                root.style.removeProperty('--v-palette-primary-light');
                root.style.removeProperty('--v-palette-primary-dark');
                root.style.removeProperty('--v-palette-primary-contrastText');
                root.style.removeProperty('--v-palette-primary-mainChannel');
                root.style.removeProperty('--v-palette-primary-lightChannel');
                root.style.removeProperty('--v-palette-primary-darkChannel');
                root.style.removeProperty('--v-palette-sellerColor-main');
                root.style.removeProperty('--v-palette-sellerColor-dark');
                root.style.removeProperty('--v-palette-sellerColor-contrastText');
                root.style.removeProperty('--v-palette-sellerColor-mainChannel');
                root.style.removeProperty('--v-palette-deeper-orange-main');
                root.style.removeProperty('--v-palette-deeper-orange-light');
                root.style.removeProperty('--v-palette-deeper-orange-dark');
                root.style.removeProperty('--v-palette-deeper-orange-contrastText');
                root.style.removeProperty('--v-palette-deeper-orange-mainChannel');
            }
        };
    }, [isSodomall]);

    const content = (
        <>
            <Helmet>
                {isSodomall ? (
                    <>
                        <title>소도몰 매장 관리자</title>
                        <meta property="og:title" content="소도몰 매장 관리자" />
                        <meta property="og:description" content="소도몰 매장 관리자 페이지" />
                        <meta property="og:image" content={`${window.location.origin}/sodomall_logo.png`} />
                    </>
                ) : (
                    <>
                        <title>DDM Live</title>
                        <meta property="og:title" content="DDM Live" />
                        <meta property="og:description" content="DDM Live 관리자 페이지" />
                        <meta property="og:image" content={`${window.location.origin}/ddm_live_logo_48.jpg`} />
                    </>
                )}
            </Helmet>
            <PageLoadingScreen isLoading={state === 'loading'} />
            <Box
                className={'flex flex-col'}
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    '--app-bar-height': `${appBarHeight}px`,
                    scrollBehavior: 'smooth',
                }}
            >
                <DDMNavBar setAppBarHeight={setAppBarHeight} />
                <MenuDrawer
                    isDrawerOpen={isDrawerOpen}
                    appBarHeight={appBarHeight}
                    ref={drawRef}
                    menuTitleList={menuTitleList}
                    // append={<AppendExample/>}
                />
                <Box
                    sx={(theme) => ({
                        flexGrow: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        // marginLeft: isDrawerOpen ? `${drawWidth}px` : 0,
                        transition: `margin 225ms cubic-bezier(0, 0, 0.2, 1) 0ms`,
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        zIndex: 0,

                        [theme.breakpoints.up('sm')]: {
                            marginLeft: isDrawerOpen ? `${drawWidth}px` : 0,
                        },
                    })}
                >
                    <Banner
                        isOpen={isBannerOpen}
                        color={'warning'}
                        prepend={
                            <NewReleases
                                sx={{
                                    fontSize: '45px',
                                }}
                            />
                        }
                        message={t('sidebar.adminApprovalPending')}
                        isAllowHtml
                        closeText={t('sidebar.confirmApproval')}
                        ButtonProps={{
                            variant: 'outlined',
                            color: 'black',
                        }}
                        appBarHeight={appBarHeight}
                        sticky
                    />

                    <Box
                        component={'section'}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            px: 3,
                            py: 2,
                            flexGrow: 1,
                            position: 'relative',
                            zIndex: 0,
                        }}
                    >
                        <Outlet />
                        <ScrollRestoration
                            getKey={(location, matches) => {
                                return location.key;
                            }}
                        />
                    </Box>
                    {!isSodomall && <Footer component={'footer'} px={3} py={1} />}
                </Box>
            </Box>

            <DialogContainer />
        </>
    );

    return content;
};

export default ManagerLayout;
